import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import { UpgradeNudge } from "@/components/UpgradeNudge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Copy, RefreshCw, Trash2, HardHat, User, ChevronRight } from "lucide-react";

export default function Team() {
  const { fleet, loadFleet, user, isManager } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [code, setCode] = useState("");

  const load = () => api.get("/fleet/members").then(({ data }) => setMembers(data));
  useEffect(() => { load(); setCode(fleet?.invite_code || ""); }, [fleet]);

  const copyCode = () => { navigator.clipboard.writeText(code); toast.success("Invite code copied"); };

  const regen = async () => {
    try { const { data } = await api.post("/fleet/regenerate-code"); setCode(data.invite_code); await loadFleet(); toast.success("New invite code generated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const remove = async (mid) => {
    try { await api.delete(`/fleet/members/${mid}`); toast.success("Worker removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="team-page">
      <PageHead title="Team" sub="Manage who's in your fleet." />

      <UpgradeNudge kind="staff" current={members.length} limit={4} />

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <div className="card">
          <div className="px-5 py-4 border-b border-white/10"><h2 className="font-head font-700 text-xl uppercase tracking-wide">Members ({members.length})</h2></div>
          <div className="divide-y divide-white/10">
            {members.map((m) => (
              <div
                key={m.id}
                onClick={fleet?.is_owner ? () => nav(`/team/${m.id}`) : undefined}
                className={`flex items-center gap-4 px-5 py-4 group ${fleet?.is_owner ? "cursor-pointer hover:bg-[#1A1C23] transition-colors" : ""}`}
                data-testid={`member-${m.id}`}
              >
                <div className="w-10 h-10 bg-[#0D0E11] border border-white/10 flex items-center justify-center">
                  {m.role === "manager" ? <HardHat className="w-5 h-5 text-[#FFB800]" /> : <User className="w-5 h-5 text-[#94A3B8]" />}
                </div>
                <div className="flex-1 min-w-0" data-testid={fleet?.is_owner ? `view-profile-${m.id}` : undefined}>
                  <p className="font-head font-700 text-lg truncate flex items-center gap-1.5 group-hover:text-[#FFB800] transition-colors">
                    {m.name} {m.id === user?.id && <span className="text-[#94A3B8] text-sm font-body">(you)</span>}
                    {fleet?.is_owner && <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#FFB800]" />}
                  </p>
                  <p className="font-mono text-xs text-[#94A3B8] truncate">{m.email} · joined {fmtDate(m.created_at)}</p>
                </div>
                <span className={`badge ${m.role === "manager" ? "badge-upcoming" : "badge-healthy"}`}>{m.role}</span>
                {isManager && m.role === "worker" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button onClick={(e) => e.stopPropagation()} className="text-[#94A3B8] hover:text-[#FF3B30] transition-colors" data-testid={`remove-member-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#1A1C23] border border-white/10 text-white" onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader><AlertDialogTitle className="font-head uppercase">Remove {m.name}?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#94A3B8]">They&apos;ll lose access to this fleet immediately.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-ghost border-white/20">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(m.id)} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80" data-testid={`confirm-remove-${m.id}`}>Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card p-6 h-fit">
          <h2 className="font-head font-700 text-xl uppercase tracking-wide">Invite Code</h2>
          <p className="font-body text-sm text-[#94A3B8] mt-1">Workers enter this when signing up to join <span className="text-white">{fleet?.name}</span>.</p>
          <div className="mt-4 bg-[#0D0E11] border border-[#FFB800]/30 p-4 flex items-center justify-between">
            <span className="font-mono text-3xl text-[#FFB800] tracking-[0.3em]" data-testid="invite-code">{code}</span>
            <button onClick={copyCode} className="text-[#94A3B8] hover:text-white" data-testid="copy-code-btn"><Copy className="w-5 h-5" /></button>
          </div>
          <button onClick={regen} className="btn-ghost w-full h-12 font-head text-sm flex items-center justify-center gap-2 mt-3" data-testid="regen-code-btn"><RefreshCw className="w-4 h-4" /> Regenerate Code</button>
        </div>
      </div>
    </div>
  );
}
